import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useState, useEffect, useCallback, useRef } from 'react';
import step1Image from '@/assets/Steps/step1.svg';
import step2Image from '@/assets/Steps/step2.svg';
import step3Image from '@/assets/Steps/step3.svg';
import arrow from '@/assets/Steps/arrow.svg';
import CustomButton from '@/components/Button/button';
import Section from '@/components/Section/section';
import { useInView } from '@/hooks/useInView';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';

const STEP_STAGGER_MS = 130;

const STEPS = [
  {
    title: 'Explore',
    description:
      'Browse the vineyards shaping the future of English & Welsh wine.',
    backgroundImage: step1Image,
  },
  {
    title: 'Plan',
    description: 'Add tastings, tours, cellar doors and events to your trip.',
    backgroundImage: step2Image,
  },
  {
    title: 'Navigate',
    description:
      'Get directions and itinerary support for the day of your visit.',
    backgroundImage: step3Image,
  },
];

interface StepSectionProps {
  onDownloadClick: () => void;
}

export default function StepSection({
  onDownloadClick,
}: StepSectionProps): JSX.Element {
  const [sectionRef, inView] = useInView({
    threshold: 0.3,
    rootMargin: '0px 0px -80px 0px',
    unobserveOnce: true,
  });

  useEffect(() => {
    if (inView) {
      gtag('event', 'section_view', {
        event_category: 'Engagement',
        event_label: 'Step Section',
      });
    }
  }, [inView]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [, setTouchEnd] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 899px)').matches
      : false
  );
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const minSwipeDistance = 50;

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentCardIndex((prev) => (prev + 1) % STEPS.length);
    }, 5000);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isMobile, startTimer]);

  const goToCard = useCallback(
    (index: number) => {
      setCurrentCardIndex(index);
      if (isMobile) startTimer();
    },
    [isMobile, startTimer]
  );

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart == null) return;

    const clientX = e.changedTouches[0]?.clientX;
    if (clientX == null) return;

    setTouchEnd(clientX);

    const distance = touchStart - clientX;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToCard((currentCardIndex + 1) % STEPS.length);
    } else if (isRightSwipe) {
      goToCard((currentCardIndex - 1 + STEPS.length) % STEPS.length);
    }
  };

  return (
    <Section
      id="how-it-works"
      ref={sectionRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: color.cream['200'],
        py: { xs: '48px', md: '64px' },
        px: { xs: '24px', md: '64px' },
      }}
    >
      <style>{`
        @keyframes stepCardIn {
          from {
            opacity: 0;
            transform: translateY(22px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes stepHeadingIn {
          from {
            opacity: 0;
            transform: translateY(18px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes stepButtonIn {
          from {
            opacity: 0;
            transform: translateY(14px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes dotProgress {
          0% {
            width: 0%;
          }
          100% {
            width: 100%;
          }
        }
      `}</style>
      <Box
        sx={{
          marginBottom: { xs: '40px', md: '64px' },
          maxWidth: '720px',
          opacity: 0,
          transform: 'translateY(18px)',
          ...(inView && {
            animation: 'stepHeadingIn 0.55s ease-out forwards',
          }),
        }}
      >
        <Typography
          component="h2"
          sx={{
            ...typography.display,
            color: color.charcoal.default,
            textAlign: 'center',
            lineHeight: 1.1,
            fontSize: {
              xs: 'var(--font-size-36, 36px)',
              sm: 'var(--font-size-48, 48px)',
              md: typography.display.fontSize,
            },
          }}
        >
          Discover and plan your
          <br />
          next trip
        </Typography>
      </Box>

      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'stretch',
          gap: '16px',
          marginBottom: { xs: '36px', md: '52px' },
          width: '100%',
          maxWidth: '1000px',
          touchAction: 'pan-y',
        }}
      >
        {STEPS.map((step, index) => (
          <Box
            key={step.title}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            sx={{
              flex: 1,
              display: {
                xs: index === currentCardIndex ? 'flex' : 'none',
                md: 'flex',
              },
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              textAlign: 'left',
              borderRadius: '12px',
              minHeight: { xs: '340px', md: '400px' },
              px: '16px',
              py: '32px',
              backgroundImage: `url(${step.backgroundImage})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              opacity: { xs: 1, md: 0 },
              willChange: 'transform, opacity',
              ...(inView && {
                animation: 'stepCardIn 0.55s ease-out forwards',
                animationDelay: `${index * STEP_STAGGER_MS}ms`,
              }),
              '@media (max-width: 899px)': {
                animation: 'none',
                opacity: 1,
              },
              touchAction: 'pan-y',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '10px',
                border: `2px solid ${color.cream['100']}33`,
                borderRadius: '100px',
                px: { xs: '12px', md: '12px' },
                py: { xs: '6px', md: '6px' },
                backgroundColor: `${color.cream['100']}0A`,
              }}
            >
              <img src={arrow} alt="arrow" aria-hidden />
              <Typography
                sx={{
                  ...typography.heading4,
                  color: color.cream['100'],
                  fontWeight: 350,
                  fontSize: {
                    xs: 'var(--font-size-18, 18px)',
                    sm: 'var(--font-size-20, 20px)',
                    md: typography.heading4.fontSize,
                  },
                }}
              >
                {step.title}
              </Typography>
            </Box>
            <Typography
              sx={{
                ...typography.heading4,
                color: color.cream['100'],
                fontWeight: 350,
                lineHeight: 1.25,
                maxWidth: { xs: '100%', md: '250px' },
                fontSize: {
                  xs: 'var(--font-size-18, 18px)',
                  sm: 'var(--font-size-20, 20px)',
                  md: typography.heading4.fontSize,
                },
              }}
            >
              {step.description}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          gap: '8px',
          justifyContent: 'center',
          alignItems: 'center',
          mb: { xs: '28px', md: '0px' },
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {STEPS.map((step, index) => (
            <Box
              key={index}
              role="button"
              tabIndex={0}
              aria-label={`Go to ${step.title} step`}
              aria-pressed={index === currentCardIndex}
              onClick={() => goToCard(index)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goToCard(index);
                }
              }}
              sx={{
                width: index === currentCardIndex ? '40px' : '8px',
                height: '8px',
                borderRadius: '100px',
                backgroundColor: color.charcoal['200'],
                cursor: 'pointer',
                marginBottom: '28px',
                transition: 'width 0.3s ease',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {index === currentCardIndex && (
                <Box
                  key={currentCardIndex}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '0%',
                    backgroundColor: color.terracotta['300'],
                    borderRadius: '100px',
                    animation: 'dotProgress 5s linear forwards',
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
        <CustomButton
          variant="contained"
          onClick={() => {
            gtag('event', 'cta_click', {
              event_category: 'Engagement',
              event_label: 'Start Discovering – Step Section',
            });
            onDownloadClick();
          }}
        >
          START DISCOVERING
        </CustomButton>
      </Box>

      {/* Desktop Button */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'center',
          opacity: 0,
          transform: 'translateY(14px)',
          ...(inView && {
            animation: 'stepButtonIn 0.5s ease-out forwards',
            animationDelay: `${STEP_STAGGER_MS * STEPS.length}ms`,
          }),
        }}
      >
        <CustomButton
          variant="contained"
          onClick={() => {
            gtag('event', 'cta_click', {
              event_category: 'Engagement',
              event_label: 'Start Discovering – Step Section',
            });
            onDownloadClick();
          }}
        >
          START DISCOVERING
        </CustomButton>
      </Box>
    </Section>
  );
}
